import * as Cmp from "portal/utils/comparators";
import { Alert, Button, Collapse, TextField, Tooltip } from "@mui/material";
import {
  classes,
  SMALL_WHITE_BUTTON,
  TEXT_FIELD_DARK,
  WHITE_BUTTON,
} from "portal/theme/theme";
import {
  getClass,
  getSequence,
  RobotClass,
  robotSort,
} from "portal/utils/robots";
import { nanoid } from "@reduxjs/toolkit";
import { RobotResponse } from "protos/portal/robots";
import { SquareIconButton } from "portal/components/SquareIconButton";
import { TransitionGroup } from "react-transition-group";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/AddOutlined";
import CheckIcon from "@mui/icons-material/Check";
import HelpIcon from "@mui/icons-material/HelpOutlined";
import React, {
  FunctionComponent,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from "react";
import RemoveIcon from "@mui/icons-material/RemoveOutlined";

interface RobotQuickSelectProps {
  allRobots: RobotResponse[];
  onSelect: (robots: RobotResponse[]) => QuickSelectResponse[];
}

export interface QuickSelectResponse {
  robot: RobotResponse;
  outcome: QuickSelectOutcome;
}

export enum QuickSelectOutcome {
  ADDED = "added",
  REMOVED = "removed",
  NOT_FOUND = "notFound",
}

// A small notification to confirm to the user exactly what action they just
// took. For instance, if reaper114 was already selected and the user types
// "114", this might say "removed reaper114".
interface Toast {
  id: string;
  body: ReactNode;
  timeout: ReturnType<typeof setTimeout> | undefined;
}

export const RobotQuickSelect: FunctionComponent<RobotQuickSelectProps> = ({
  allRobots: robotsUnsorted,
  onSelect,
}) => {
  const { t } = useTranslation();

  const robots = useMemo(() => {
    return robotsUnsorted.toSorted(robotSort);
  }, [robotsUnsorted]);

  const [isOpen, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const inputRef = useRef<HTMLInputElement>();

  const open = (): void => {
    setOpen(true);
  };
  const close = (): void => {
    setOpen(false);
    setInputValue("");
    setToasts([]);
  };

  const pushToasts = (bodies: ReactNode[]): void => {
    const newToasts = [...toasts];
    for (const body of bodies) {
      const id = nanoid();
      const timeout = setTimeout(() => {
        setToasts((toasts) => toasts.filter((t) => t.id !== id));
      }, 2000);
      newToasts.push({ id, body, timeout });
    }

    const MAX_TOASTS = 3;
    const removeCount = Math.max(0, newToasts.length - MAX_TOASTS);
    const removed = newToasts.splice(0, removeCount);
    for (const t of removed) {
      clearTimeout(t.timeout);
    }
    setToasts(newToasts);
  };

  const getMatchedRobot = (value: string): RobotResponse | undefined => {
    // If a full serial was given, match it.
    const exactMatch = robots.find((robot) => robot.serial === value);
    if (exactMatch) {
      return exactMatch;
    }

    // If an integer with class was given, match a robot from that class.
    const reMatch = value.match(/^(S|R|T|D|DS)([0-9]+)$/i);
    if (reMatch && reMatch[1] && reMatch[2] /* appease tsc */) {
      const robotClass = {
        S: RobotClass.Slayers,
        R: RobotClass.Reapers,
        T: RobotClass.Rtcs,
        D: RobotClass.Simulators,
        DS: RobotClass.Simulators,
      }[reMatch[1].toUpperCase()];
      const sequence = Number(reMatch[2]);
      const matches = robots.filter(
        (robot) =>
          getClass(robot) === robotClass &&
          getSequence(robot.serial) === sequence
      );
      if (matches.length > 0) {
        return matches[0];
      }
    }

    // If a bare integer was given, match slayer1 through slayer113, or
    // reaper114+.
    if (/^[0-9]+$/.test(value)) {
      const sequence = Number(value);
      const matches = robots
        .filter((robot) => getSequence(robot.serial) === sequence)
        .sort(
          // Prioritize sequences that look like they're in "the right class".
          // That is, we might have some wacky "reaper1" or "slayer150" robots,
          // but we'd rather match "slayer1" or "reaper150" instead.
          Cmp.comparing((robot) => {
            const sequence = getSequence(robot.serial);
            const robotClass = getClass(robot);

            const LAST_SLAYER = 113;
            const FIRST_REAPER = LAST_SLAYER + 1;

            let expected: RobotClass | undefined;
            if (sequence >= 1 && sequence <= LAST_SLAYER) {
              expected = RobotClass.Slayers;
            } else if (sequence >= FIRST_REAPER) {
              expected = RobotClass.Reapers;
            }
            return robotClass === expected ? 1 : 2;
          })
        );
      if (matches.length > 0) {
        return matches[0];
      }
    }
  };

  const commit = (inputs: string[]): void => {
    const matchedRobots = [];
    const unmatchedInputs = [];
    for (const input of inputs) {
      if (!input) {
        continue;
      }
      const match = getMatchedRobot(input);
      if (match) {
        matchedRobots.push(match);
      } else {
        unmatchedInputs.push(input);
      }
    }

    const results: Record<QuickSelectOutcome, Array<RobotResponse>> = {
      [QuickSelectOutcome.ADDED]: [],
      [QuickSelectOutcome.REMOVED]: [],
      [QuickSelectOutcome.NOT_FOUND]: [],
    };
    for (const { robot, outcome } of onSelect(matchedRobots)) {
      results[outcome].push(robot);
    }

    const toastBodies: ReactNode[] = [];
    const formatNames = (names: string[]): string => {
      const [first, ...rest] = names;
      if (rest.length === 0) {
        return first ?? ""; // shouldn't be absent
      }
      return t("utils.lists.andMore", { first, remaining: rest.length });
    };
    if (results[QuickSelectOutcome.ADDED].length > 0) {
      const names = results[QuickSelectOutcome.ADDED].map((r) => r.serial);
      toastBodies.push(
        <Alert className="mb-1 py-0" icon={<AddIcon />}>
          {formatNames(names)}
        </Alert>
      );
    }
    if (results[QuickSelectOutcome.REMOVED].length > 0) {
      const names = results[QuickSelectOutcome.REMOVED].map((r) => r.serial);
      toastBodies.push(
        <Alert className="mb-1 py-0" icon={<RemoveIcon />}>
          {formatNames(names)}
        </Alert>
      );
    }
    const failedNames = [
      ...unmatchedInputs,
      ...results[QuickSelectOutcome.NOT_FOUND].map((r) => r.serial),
    ];
    if (failedNames.length > 0) {
      toastBodies.push(
        <Alert className="mb-1 py-0" severity="warning">
          {t("views.admin.config.bulk.quickSelect.notFound", {
            name: formatNames(failedNames.map((s) => JSON.stringify(s))),
          })}
        </Alert>
      );
    }

    pushToasts(toastBodies);
    setInputValue("");
  };

  if (!isOpen) {
    return (
      <Button
        {...SMALL_WHITE_BUTTON}
        className={classes(SMALL_WHITE_BUTTON.className, "h-8")}
        onClick={open}
      >
        {t("views.admin.config.bulk.quickSelect.name")}
      </Button>
    );
  }

  return (
    <div className="relative flex">
      <div className="absolute z-above-page bottom-full -translate-y-1 left-0 right-0">
        <TransitionGroup>
          {toasts.map((toast) => (
            <Collapse key={toast.id}>{toast.body}</Collapse>
          ))}
        </TransitionGroup>
      </div>
      <TextField
        inputRef={inputRef}
        autoFocus /* only on explicit user action */ // eslint-disable-line jsx-a11y/no-autofocus
        size="small"
        {...TEXT_FIELD_DARK}
        className="max-w-40 h-8"
        value={inputValue}
        onChange={(e) => {
          // When a key is typed with a keyboard, the `onKeyDown` event will
          // process first, so usually this handler will just clear out the
          // input field. But it's still useful for non-keyboard entry methods,
          // most prominently pasting but also e.g. dictation.
          const value = e.currentTarget.value;
          const serials = splitSerials(value, true);
          if (serials) {
            commit(serials);
            return;
          }
          setInputValue(removeDelimiters(value));
        }}
        slotProps={{
          input: {
            ...TEXT_FIELD_DARK.slotProps?.input,
            className: classes(
              (
                TEXT_FIELD_DARK.slotProps?.input as
                  | { className?: string }
                  | undefined
              )?.className,
              "h-8 rounded-e-none"
            ),
            // `onKeyDown` has more precise types on `Input` than on `TextField`,
            // so put this listener here.
            onKeyDown: (e) => {
              if (e.code === "Escape") {
                close();
                return;
              }

              const terminalCodes = ["Space", "Enter", "Comma"];
              if (!terminalCodes.includes(e.code)) {
                return;
              }
              const value = e.currentTarget.value;
              const serials = splitSerials(value, false);
              if (serials) {
                commit(serials);
              }
              if (e.code === "Enter") {
                close();
              }
            },
          },
        }}
      ></TextField>
      <SquareIconButton
        theme={WHITE_BUTTON}
        className="h-8 sm:h-8 p-0 rounded-none"
        onClick={close}
      >
        <CheckIcon fontSize="inherit" />
      </SquareIconButton>
      <Tooltip arrow title={t("views.admin.config.bulk.quickSelect.help")}>
        <SquareIconButton
          theme={WHITE_BUTTON}
          className="h-8 sm:h-8 p-0 rounded-s-none border-l border-solid border-l-gray-300"
          onClick={() => inputRef.current?.focus()}
        >
          <HelpIcon fontSize="inherit" />
        </SquareIconButton>
      </Tooltip>
    </div>
  );
};

const splitSerials = (
  value: string,
  requireDelimiters: boolean
): string[] | undefined => {
  const parts = value.split(/[ ,]+/);
  if (requireDelimiters && parts.length <= 1) {
    return; // no delimiters
  }
  return parts.filter(Boolean);
};

const removeDelimiters = (value: string): string => {
  return value.replaceAll(/[ ,]/g, "");
};
